import { useNavigate, useParams } from 'react-router-dom';
import { useSport, useUpdateSport, useTranslation } from '@/hooks';
import { CreateSportRequest } from '@/types/model';
import SportsForm from '@/components/forms/SportsForm';

const EditSport = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { data: sport, isLoading, error } = useSport(id || '');
  const updateSportMutation = useUpdateSport();

  const handleSubmit = async (data: CreateSportRequest) => {
    if (!id) return;

    try {
      const result = await updateSportMutation.mutateAsync({ id, data });
      if (result.success) {
        // Navigate to the sports list page after successful update
        navigate('/content/sports', {
          state: { message: t.sports.messages.updated }
        });
      }
    } catch (error) {
      console.error('Error updating sport:', error);
      throw error; // Re-throw to let the form handle the error display
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sport) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t.errors.loadingError}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  {error instanceof Error ? error.message : 'Sport not found'}
                </p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/content/sports')}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  ← {t.sports.title}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SportsForm
          onSuccess={handleSubmit}
          onCancel={handleCancel}
          initialData={sport}
          sportId={sport.id}
          isEdit={true}
          isSubmitting={updateSportMutation.isPending}
        />
      </div>
    </div>
  );
};

export default EditSport;
